// src/services/paymentService.js
// Paiement mobile — initiation, polling, annulation

import api from './api';

const POLL_INTERVAL_MS = 10000;
const TIMEOUT_MS = 15 * 60 * 1000;

let activePollTimer = null;
let activePollExternalId = null;

const paymentService = {
  POLL_INTERVAL_MS,
  TIMEOUT_MS,

  async initiatePayment(reservationId, paymentMode, clientPhone) {
    try {
      const response = await api.post(`/reservations/${reservationId}/pay-deposit`, {
        payment_mode: paymentMode,
        client_phone: clientPhone,
      });
      const data = response.data;
      return {
        success: data.success !== false,
        status: data.status || 'SUCCESS',
        externalTransactionId: data.externalTransactionId || null,
        deepLinkUrl: data.deepLinkUrl || null,
        notificationMessage: data.notificationMessage || null,
        message: data.message,
        amount: data.amount,
        paymentMode: data.paymentMode || paymentMode,
      };
    } catch (error) {
      const networkMessage = !error.response
        ? (error.code === 'ECONNABORTED'
          ? 'Le serveur met trop de temps à répondre.'
          : 'Impossible de joindre le serveur. Vérifiez votre connexion.')
        : null;
      return {
        success: false,
        message: error.response?.data?.message || networkMessage || 'Erreur lors du paiement',
      };
    }
  },

  async checkPaymentStatus(externalTransactionId) {
    try {
      const response = await api.get(`/payments/${externalTransactionId}/status`);
      return {
        success: true,
        status: response.data.status,
        reservationId: response.data.reservationId,
        amount: response.data.amount,
      };
    } catch (error) {
      return {
        success: false,
        status: 'ERROR',
        message: error.response?.data?.message || 'Impossible de vérifier le statut',
      };
    }
  },

  async cancelPayment(externalTransactionId) {
    try {
      const response = await api.post(`/payments/${externalTransactionId}/cancel`);
      return {
        success: true,
        status: response.data.status,
        message: response.data.message,
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Erreur annulation',
      };
    }
  },

  stopPolling() {
    if (activePollTimer) {
      clearInterval(activePollTimer);
      activePollTimer = null;
    }
    activePollExternalId = null;
  },

  /**
   * Polling toutes les 10s jusqu'à SUCCESS / FAILLED / CANCELED ou timeout 15 min
   */
  startPolling(externalTransactionId, { onUpdate, onSuccess, onFailed, onTimeout }) {
    this.stopPolling();
    activePollExternalId = externalTransactionId;
    const startedAt = Date.now();

    const tick = async () => {
      if (activePollExternalId !== externalTransactionId) return;

      if (Date.now() - startedAt >= TIMEOUT_MS) {
        this.stopPolling();
        onTimeout?.();
        return;
      }

      const result = await this.checkPaymentStatus(externalTransactionId);
      if (!result.success) return;

      onUpdate?.(result);

      if (result.status === 'SUCCESS') {
        this.stopPolling();
        onSuccess?.(result);
      } else if (['FAILLED', 'CANCELED', 'REFUNDED'].includes(result.status)) {
        this.stopPolling();
        onFailed?.(result);
      }
    };

    tick();
    activePollTimer = setInterval(tick, POLL_INTERVAL_MS);
  },
};

export default paymentService;
